import { formatDate } from '../../../functions/utils';
import { Header } from '../../Table';

// Función para formatear valores especiales para exportación
export const formatValueForExport = (value: any, type?: string) => {
  if (!type) return value;

  switch (type) {
    case 'dual-text':
      if (
        value &&
        typeof value === 'object' &&
        value.primary &&
        value.secondary
      ) {
        return `${value.primary} - ${value.secondary}`;
      }
      return value;
    case 'payment-date':
      if (value && typeof value === 'object' && value.amount !== undefined) {
        const amount = value.amount;
        const date = value.date ? formatDate(value.date) : '';
        return date ? `${amount} - ${date}` : amount;
      }
      return 'Sin información';
    case 'date':
    case 'eight-days-date':
    case 'due-date':
    case 'cession-lights-false':
      return value ? formatDate(value) : 'Sin información';
    case 'money':
      return value;
    case 'cessionaries':
      return value[0]?.cessionaryName ?? '--';
    default:
      return value;
  }
};

// Configuración por defecto para tipos especiales
const DEFAULT_SPECIAL_TYPE_CONFIG = {
  'dual-text': {
    primary: {
      suffix: '_primary',
      defaultLabelSuffix: ' (Principal)',
      type: 'text',
    },
    secondary: {
      suffix: '_secondary',
      defaultLabelSuffix: ' (Secundario)',
      type: 'text',
    },
  },
  'payment-date': {
    amount: {
      suffix: '_amount',
      defaultLabelSuffix: ' (Monto)',
      type: 'money',
    },
    date: {
      suffix: '_date',
      defaultLabelSuffix: ' (Fecha)',
      type: 'date',
    },
  },
};

// Función para expandir headers especiales
export const expandHeaders = (headers: Header[]) => {
  const expandedHeaders: Header[] = [];

  headers.forEach((header) => {
    const config =
      DEFAULT_SPECIAL_TYPE_CONFIG[
        header.type as keyof typeof DEFAULT_SPECIAL_TYPE_CONFIG
      ];

    if (config) {
      // Expandir el header en múltiples columnas
      Object.entries(config).forEach(([key, configItem]) => {
        // Usar nombres personalizados si están definidos en el header
        let columnLabel = header.label;

        if (header.type === 'dual-text') {
          if (key === 'primary' && header.primaryLabel) {
            columnLabel = header.primaryLabel;
          } else if (key === 'secondary' && header.secondaryLabel) {
            columnLabel = header.secondaryLabel;
          } else {
            columnLabel = `${header.label}${configItem.defaultLabelSuffix}`;
          }
        } else if (header.type === 'payment-date') {
          if (key === 'amount' && header.amountLabel) {
            columnLabel = header.amountLabel;
          } else if (key === 'date' && header.dateLabel) {
            columnLabel = header.dateLabel;
          } else {
            columnLabel = `${header.label}${configItem.defaultLabelSuffix}`;
          }
        }

        expandedHeaders.push({
          ...header,
          key: `${header.key}${configItem.suffix}`,
          label: columnLabel,
          type: configItem.type,
        });
      });
    } else {
      expandedHeaders.push(header);
    }
  });

  return expandedHeaders;
};

// Función para expandir datos según los headers expandidos
export const expandData = (data: any[], originalHeaders: Header[]) => {
  return data.map((row) => {
    const expandedRow: any = {};

    originalHeaders.forEach((header) => {
      const config =
        DEFAULT_SPECIAL_TYPE_CONFIG[
          header.type as keyof typeof DEFAULT_SPECIAL_TYPE_CONFIG
        ];

      if (config) {
        // Expandir los datos según el tipo especial
        if (header.type === 'dual-text') {
          const value = row[header.key];
          if (
            value &&
            typeof value === 'object' &&
            value.primary &&
            value.secondary
          ) {
            expandedRow[`${header.key}_primary`] = value.primary;
            expandedRow[`${header.key}_secondary`] = value.secondary;
          } else {
            expandedRow[`${header.key}_primary`] = '';
            expandedRow[`${header.key}_secondary`] = '';
          }
        } else if (header.type === 'payment-date') {
          const value = row[header.key];
          if (
            value &&
            typeof value === 'object' &&
            value.amount !== undefined
          ) {
            expandedRow[`${header.key}_amount`] = value.amount;
            expandedRow[`${header.key}_date`] = value.date;
          } else {
            expandedRow[`${header.key}_amount`] = 0;
            expandedRow[`${header.key}_date`] = null;
          }
        }
      } else {
        expandedRow[header.key] = row[header.key];
      }
    });

    return expandedRow;
  });
};

// Función para reconstruir headers originales desde headers expandidos
export const reconstructOriginalHeaders = (expandedHeaders: Header[]) => {
  const originalHeaders: Header[] = [];
  const processedKeys = new Set<string>();

  expandedHeaders.forEach((header) => {
    // Extraer la clave original removiendo sufijos
    let originalKey = header.key;
    let originalType = header.type;

    if (header.key.includes('_primary') || header.key.includes('_secondary')) {
      originalKey = header.key.replace(/_primary$|_secondary$/, '');
      originalType = 'dual-text';
    } else if (header.key.includes('_amount') || header.key.includes('_date')) {
      originalKey = header.key.replace(/_amount$|_date$/, '');
      originalType = 'payment-date';
    }

    if (!processedKeys.has(originalKey)) {
      processedKeys.add(originalKey);

      // Encontrar todos los headers expandidos para esta clave original
      const relatedHeaders = expandedHeaders.filter((h) => {
        if (
          originalKey.includes('_primary') ||
          originalKey.includes('_secondary')
        ) {
          return h.key.replace(/_primary$|_secondary$/, '') === originalKey;
        } else if (
          originalKey.includes('_amount') ||
          originalKey.includes('_date')
        ) {
          return h.key.replace(/_amount$|_date$/, '') === originalKey;
        }
        return h.key === originalKey;
      });

      // Reconstruir el header original
      const originalHeader: Header = {
        key: originalKey,
        label: header.label.replace(
          / \(Principal\)| \(Secundario\)| \(Monto\)| \(Fecha\)$/,
          '',
        ),
        type: originalType,
      };

      // Agregar propiedades personalizadas si existen
      if (originalType === 'dual-text') {
        const primaryHeader = relatedHeaders.find((h) =>
          h.key.includes('_primary'),
        );
        const secondaryHeader = relatedHeaders.find((h) =>
          h.key.includes('_secondary'),
        );
        if (primaryHeader) originalHeader.primaryLabel = primaryHeader.label;
        if (secondaryHeader)
          originalHeader.secondaryLabel = secondaryHeader.label;
      } else if (originalType === 'payment-date') {
        const amountHeader = relatedHeaders.find((h) =>
          h.key.includes('_amount'),
        );
        const dateHeader = relatedHeaders.find((h) => h.key.includes('_date'));
        if (amountHeader) originalHeader.amountLabel = amountHeader.label;
        if (dateHeader) originalHeader.dateLabel = dateHeader.label;
      }

      originalHeaders.push(originalHeader);
    }
  });

  return originalHeaders;
};

// Función para agregar nuevos tipos especiales
export const addSpecialTypeConfig = (typeName: string, config: any) => {
  DEFAULT_SPECIAL_TYPE_CONFIG[
    typeName as keyof typeof DEFAULT_SPECIAL_TYPE_CONFIG
  ] = config;
};
